/**
 * Debug Logger Utility
 * Provides consistent logging with context and filtering.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export interface LogContext {
  component: string;
  propertyId?: string;
  userId?: string;
  action?: string;
}

interface DebugLoggerConsole {
  log: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  group: (title: string) => void;
  groupEnd: () => void;
  table: (data: unknown[], columns?: string[]) => void;
  time: (label: string) => void;
  timeEnd: (label: string) => void;
}

interface PropertySubscriptionDebugRow {
  property_id: string;
  tier?: string;
}

interface DebugLoggerControls {
  enable: () => DebugLogger;
  disable: () => void;
  setLevel: (level: keyof typeof LogLevel) => void;
  setFilter: (components: string[]) => void;
  logger: DebugLogger;
}

declare global {
  interface Window {
    debugLogger?: DebugLoggerControls;
  }
}

type HeaderMap = Record<string, unknown> & { Authorization?: string };
type LogPayload = unknown;

const logSink: DebugLoggerConsole = globalThis.console;

class DebugLogger {
  private enabled: boolean;
  private level: LogLevel;
  private filters: string[];

  constructor() {
    const readStorage = (key: string): string | null => {
      try {
        if (typeof window === 'undefined' || !window.localStorage) {
          return null;
        }
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    };

    this.enabled = process.env.NODE_ENV === 'development' || readStorage('debug') === 'true';
    const levelStr = readStorage('logLevel') || 'INFO';
    this.level = LogLevel[levelStr as keyof typeof LogLevel] || LogLevel.INFO;
    const filterStr = readStorage('logFilter') || '';
    this.filters = filterStr ? filterStr.split(',') : [];
  }

  private shouldLog(level: LogLevel, component: string): boolean {
    if (!this.enabled) return false;
    if (level < this.level) return false;
    if (this.filters.length > 0 && !this.filters.includes(component)) return false;
    return true;
  }

  private formatMessage(context: LogContext, message: string): string {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const prefix = `[${timestamp}] [${context.component}]`;
    let fullMessage = `${prefix} ${message}`;
    if (context.propertyId) {
      fullMessage += ` | Property: ${context.propertyId}`;
    }
    if (context.action) {
      fullMessage += ` | Action: ${context.action}`;
    }
    return fullMessage;
  }

  debug(context: LogContext, message: string, data?: LogPayload): void {
    if (this.shouldLog(LogLevel.DEBUG, context.component)) {
      logSink.log(this.formatMessage(context, message), data || '');
    }
  }

  info(context: LogContext, message: string, data?: LogPayload): void {
    if (this.shouldLog(LogLevel.INFO, context.component)) {
      logSink.info(this.formatMessage(context, message), data || '');
    }
  }

  warn(context: LogContext, message: string, data?: LogPayload): void {
    if (this.shouldLog(LogLevel.WARN, context.component)) {
      logSink.warn(this.formatMessage(context, message), data || '');
    }
  }

  error(context: LogContext, message: string, error?: LogPayload): void {
    if (!this.shouldLog(LogLevel.ERROR, context.component)) return;
    logSink.error(this.formatMessage(context, message), error || '');
    if (error && typeof error === 'object' && 'stack' in error && typeof error.stack === 'string') {
      logSink.error('Stack trace:', error.stack);
    }
  }

  group(title: string): void {
    if (this.enabled) {
      logSink.group(title);
    }
  }

  groupEnd(): void {
    if (this.enabled) {
      logSink.groupEnd();
    }
  }

  table(data: unknown[], columns?: string[]): void {
    if (this.enabled) {
      if (columns) {
        logSink.table(data, columns);
      } else {
        logSink.table(data);
      }
    }
  }

  debugPropertyMatching(searchId: string, subscriptions: PropertySubscriptionDebugRow[]): void {
    if (!this.enabled || this.level > LogLevel.DEBUG) return;
    this.group(`Property ID Matching: "${searchId}"`);
    const results = subscriptions.map((sub) => {
      const normalized = this.normalizePropertyId(sub.property_id);
      const searchNormalized = this.normalizePropertyId(searchId);
      return {
        'Subscription ID': sub.property_id,
        Normalized: normalized,
        'Search Normalized': searchNormalized,
        Match: normalized === searchNormalized ? 'yes' : 'no',
        Tier: sub.tier,
      };
    });
    this.table(results);
    this.groupEnd();
  }

  private normalizePropertyId(id: string): string {
    return id.trim().toLowerCase().replace(/[-_.]/g, '');
  }

  logApiCall(method: string, url: string, body?: LogPayload, headers?: HeaderMap): void {
    if (!this.enabled) return;
    this.group(`API ${method} ${url}`);
    if (headers) {
      const safeHeaders = { ...headers };
      if (safeHeaders.Authorization) {
        safeHeaders.Authorization = 'Bearer [REDACTED]';
      }
      logSink.log('Headers:', safeHeaders);
    }
    if (body) {
      logSink.log('Body:', body);
    }
    this.groupEnd();
  }

  logApiResponse(url: string, status: number, data?: LogPayload, error?: LogPayload): void {
    if (!this.enabled) return;
    const logMethod = status >= 400 ? logSink.error : logSink.log;
    this.group(`API Response: ${url}`);
    logMethod(`Status: ${status}`);
    if (data) {
      logSink.log('Data:', data);
    }
    if (error) {
      logSink.error('Error:', error);
    }
    this.groupEnd();
  }

  startTimer(label: string): void {
    if (this.enabled) {
      logSink.time(label);
    }
  }

  endTimer(label: string): void {
    if (this.enabled) {
      logSink.timeEnd(label);
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    localStorage.setItem('debug', enabled ? 'true' : 'false');
  }

  setLevel(level: LogLevel): void {
    this.level = level;
    localStorage.setItem('logLevel', LogLevel[level]);
  }

  setFilter(components: string[]): void {
    this.filters = components;
    localStorage.setItem('logFilter', components.join(','));
  }

  static enableDebug(): DebugLogger {
    const logger = new DebugLogger();
    logger.setEnabled(true);
    logger.setLevel(LogLevel.DEBUG);
    logSink.log('Debug mode enabled. Use logger.setFilter(["component"]) to filter logs.');
    return logger;
  }
}

export const logger = new DebugLogger();

if (typeof window !== 'undefined') {
  window.debugLogger = {
    enable: () => DebugLogger.enableDebug(),
    disable: () => logger.setEnabled(false),
    setLevel: (level: keyof typeof LogLevel) => logger.setLevel(LogLevel[level]),
    setFilter: (components: string[]) => logger.setFilter(components),
    logger,
  };
}
